import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getToken } from '@/configs/coalEduAuth';
import { toSSOLogin, checkLogin } from '@/configs/coalEduAuth';
import { ElMessageBox } from 'element-plus';

// 用户信息类型定义
export interface UserInfo {
  ID?: number;
  redirectURI?: string | null;
  token?: string;
  expire?: string;
  userName?: string;
  realName?: string;
  mobile?: string;
  enterpriseName?: string;
  enterpriseID?: number;
  enterpriseInnercode?: string;
  userType?: string | number;
  privs?: any;
  consummate?: boolean;
  zsflDimensionID?: number;
  ksclDimensionID?: number;
  examDimensionID?: number;
  jndsDimensionID?: number;
  jfscDimensionID?: number;
  qyflDimensionID?: number;
  isYanKuangUser?: string;
  isVip?: number;
  isTopEnterprise?: number;
  isVerifyEnable?: number;
  enterpriseTreeLevel?: number;
  isMaster?: number;
  masterStatus?: number;
  screenList?: Array<{
    key: number;
    value: string;
  }>;
  isMasterManager?: number;
  isDashboardSuperManager?: number;
  avatarSrc?: string;
  hasSelfMade?: string;
  hasExportMemberStudy?: string;
  // 这个角色名称仅用作展示，后端会返回权重最高的角色名称
  displayRoleType?: string;
  [key: string]: any;
}

// 企业权限类型定义
export interface EnterprisePermission {
  enterpriseMember: 0 | 1;
  enterpriseManager: 0 | 1;
}

export const useUserStore = defineStore('user', () => {
  // 状态
  const isLogin = ref<boolean>(false);
  const userInfo = ref<UserInfo>({});
  const token = ref<string>('');
  const enterprisePermission = ref<EnterprisePermission>({
    enterpriseMember: 0,
    enterpriseManager: 0,
  });

  // 计算属性
  const userRole = computed(() => {
    return userInfo.value?.displayRoleType || '普通用户';
  });

  const isEnterpriseMember = computed(() => enterprisePermission.value.enterpriseMember === 1);
  const isEnterpriseManager = computed(() => enterprisePermission.value.enterpriseManager === 1);
  const isDashboardSuperManager = computed(() => userInfo.value.isDashboardSuperManager === 1);

  const setLoginState = (loginState: boolean) => {
    isLogin.value = loginState;
  };

  const setUserInfo = (info: UserInfo) => {
    userInfo.value = { ...info };
  };

  const setToken = (newToken: string) => {
    token.value = newToken;
  };

  const setEnterprisePermission = (permission: EnterprisePermission) => {
    enterprisePermission.value = { ...permission };
  };

  const handleLoginSuccess = (info: UserInfo, newToken: string) => {
    setLoginState(true);
    setUserInfo(info);
    setToken(newToken);

    // 如果有企业信息，设置企业权限
    //  userType 1 是企业管理   99 是超管
    if (info.enterpriseID) {
      setEnterprisePermission({
        enterpriseMember: 1,
        enterpriseManager: String(info.userType) === '99' || String(info.userType) === '1' ? 1 : 0,
      });
    }
  };

  const handleLogout = () => {
    setLoginState(false);
    setUserInfo({});
    setToken('');
    setEnterprisePermission({
      enterpriseMember: 0,
      enterpriseManager: 0,
    });
  };

  const updateUserInfo = (updates: Partial<UserInfo>) => {
    userInfo.value = { ...userInfo.value, ...updates };
  };

  const checkLoginStatus = () => {
    return isLogin.value && !!token.value && !!userInfo.value.userName;
  };

  const getUserDisplayName = () => {
    return userInfo.value.userName || '未知用户';
  };

  const getEnterpriseInfo = () => {
    return {
      enterpriseID: userInfo.value.enterpriseID,
      enterpriseInnercode: userInfo.value.enterpriseInnercode,
      enterpriseName: userInfo.value.enterpriseName,
    };
  };

  // 检查登录状态并处理未登录情况
  const checkLoginAndRedirect = async (): Promise<boolean> => {
    // 在本地环境下只要存在 token 就直接视为通过
    if (window?.location?.origin?.startsWith('http://localhost')) {
      const currentToken = token.value || getToken();
      if (currentToken) {
        return true;
      }
    }
    await checkLogin();
    if (!isLogin.value) {
      try {
        await ElMessageBox.confirm('您暂未登录，是否登录？', '提示', {
          confirmButtonText: '登录',
          cancelButtonText: '取消',
          type: 'warning',
        }).then(() => {
          toSSOLogin(null);
        });
        return false;
      } catch {
        return false;
      }
    }
    return true;
  };

  return {
    isLogin,
    userInfo,
    token,
    enterprisePermission,
    userRole,
    isEnterpriseMember,
    isEnterpriseManager,
    isDashboardSuperManager,
    checkLogin,
    setLoginState,
    setUserInfo,
    setToken,
    setEnterprisePermission,
    handleLoginSuccess,
    handleLogout,
    updateUserInfo,
    checkLoginStatus,
    getUserDisplayName,
    getEnterpriseInfo,
    checkLoginAndRedirect,
  };
});
